import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import manualPaymentVerificationService from '../../../services/manualPaymentVerificationService';

const AuditLogViewer = () => {
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadAuditLogs();
  }, []);

  const loadAuditLogs = async () => {
    try {
      setLoading(true);
      setError('');
      const logs = await manualPaymentVerificationService?.getRecentVerificationActivity(50);
      setAuditLogs(logs);
    } catch (err) {
      setError(err?.message || 'Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  const getActionIcon = (actionType) => {
    const icons = {
      billing_updated: 'DollarSign',
      employee_updated: 'UserCheck',
      tier_changed: 'TrendingUp'
    };
    return icons?.[actionType] || 'Activity';
  };

  const getActionColor = (actionType) => {
    const colors = {
      billing_updated: 'text-green-600 bg-green-50',
      employee_updated: 'text-blue-600 bg-blue-50',
      tier_changed: 'text-purple-600 bg-purple-50'
    };
    return colors?.[actionType] || 'text-gray-600 bg-gray-50';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Icon name="Loader2" className="w-6 h-6 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Icon name="FileText" className="w-5 h-5 text-primary" />
          <h3 className="text-lg font-semibold text-foreground">Verification Activity Log</h3>
        </div>
        <Button size="sm" variant="outline" onClick={loadAuditLogs}>
          <Icon name="RefreshCw" size={14} className="mr-1" />
          Refresh
        </Button>
      </div>

      {error && (
        <div className="bg-error/10 border border-error/20 rounded-lg p-3">
          <p className="text-sm text-error">{error}</p>
        </div>
      )}

      <div className="bg-card rounded-lg border border-border">
        <div className="divide-y divide-border max-h-[600px] overflow-y-auto">
          {auditLogs?.length === 0 ? (
            <div className="p-8 text-center">
              <Icon name="FileText" className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">No audit logs available</p>
            </div>
          ) : (
            auditLogs?.map((log) => (
              <div key={log?.id} className="p-4 hover:bg-muted/30 transition-colors">
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${getActionColor(log?.actionType)}`}>
                    <Icon name={getActionIcon(log?.actionType)} size={18} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-1">
                      <div>
                        <p className="text-sm font-semibold text-foreground">
                          {log?.actionType?.replace(/_/g, ' ')?.toUpperCase()}
                        </p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          by {log?.performedBy}
                        </p>
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {new Date(log?.createdAt)?.toLocaleString()}
                      </span>
                    </div>
                    {log?.changes && (
                      <div className="mt-2 bg-muted/50 rounded p-2">
                        <p className="text-xs font-mono text-muted-foreground">
                          {JSON.stringify(log?.changes, null, 2)}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Export Options */}
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">
          Showing {auditLogs?.length} recent verification activities
        </p>
        <Button size="sm" variant="outline">
          <Icon name="Download" size={14} className="mr-1" />
          Export Full Report
        </Button>
      </div>
    </div>
  );
};

export default AuditLogViewer;